import { getAddress, isAddress as viemIsAddress, zeroAddress } from 'viem'
import { ChainId, Percent, Token, CurrencyAmount, Currency } from '@etcswapv2/sdk-legacy'
import { TokenAddressMap } from '../state/lists/hooks'

// returns the checksummed address if the address is valid, otherwise returns false
export function isAddress(value: string | undefined | null): `0x${string}` | false {
  if (!value) return false
  try {
    if (viemIsAddress(value)) {
      return getAddress(value)
    }
    return false
  } catch {
    return false
  }
}

// Block explorer URLs per chain
const BLOCK_EXPLORER_URLS: { [chainId: number]: string } = {
  61: 'https://etc.blockscout.com',
  63: 'https://etc-mordor.blockscout.com',
}

export function getEtherscanLink(chainId: ChainId, data: string, type: 'transaction' | 'token' | 'address'): string {
  const prefix = BLOCK_EXPLORER_URLS[chainId] || BLOCK_EXPLORER_URLS[61]
  switch (type) {
    case 'transaction': {
      return `${prefix}/tx/${data}`
    }
    case 'token': {
      return `${prefix}/address/${data}`
    }
    case 'address':
    default: {
      return `${prefix}/address/${data}`
    }
  }
}

// shorten the checksummed version of the input address to have 0x + 4 characters at start and end
export function shortenAddress(address: string, chars = 4): string {
  const parsed = isAddress(address)
  if (!parsed) {
    throw Error(`Invalid 'address' parameter '${address}'.`)
  }
  return `${parsed.substring(0, chars + 2)}...${parsed.substring(42 - chars)}`
}

// add 10%
export function calculateGasMargin(value: bigint): bigint {
  return (value * 11000n) / 10000n
}

// converts a basis points value to a sdk percent
export function basisPointsToPercent(num: number): Percent {
  return new Percent(BigInt(num), BigInt(10000))
}

export function calculateSlippageAmount(value: CurrencyAmount, slippage: number): [bigint, bigint] {
  if (slippage < 0 || slippage > 10000) {
    throw Error(`Unexpected slippage value: ${slippage}`)
  }
  return [
    (value.raw * BigInt(10000 - slippage)) / 10000n,
    (value.raw * BigInt(10000 + slippage)) / 10000n
  ]
}

export function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}

// Format a currency amount with commas, NEVER using scientific notation
export function formatCurrencyAmount(amount: CurrencyAmount | undefined, sigFigs = 6): string {
  if (!amount) return '-'

  const exact = amount.toExact()
  const num = parseFloat(exact)

  // Handle zero
  if (num === 0) return '0'

  // For very small numbers (< 0.0001), show fixed decimals without scientific notation
  if (num < 0.0001 && num > 0) {
    // Use toFixed with enough precision, then trim trailing zeros
    const fixed = num.toFixed(sigFigs + 6) // Extra precision for tiny numbers
    // Remove trailing zeros but keep at least sigFigs significant figures
    const trimmed = fixed.replace(/\.?0+$/, '')
    // If result is too long, truncate to reasonable display
    if (trimmed.length > 12) {
      return '< 0.0001'
    }
    return trimmed
  }

  // For larger numbers, format with commas and limited decimals
  const parts = exact.split('.')
  const integerPart = parts[0]
  const decimalPart = parts[1]

  // Add thousand separators to integer part
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  if (!decimalPart) {
    return formattedInteger
  }

  // Limit decimal places based on magnitude
  let maxDecimals = 6
  if (num >= 1000) maxDecimals = 2
  else if (num >= 1) maxDecimals = 4

  const trimmedDecimal = decimalPart.slice(0, maxDecimals).replace(/0+$/, '')

  return trimmedDecimal ? `${formattedInteger}.${trimmedDecimal}` : formattedInteger
}

export function isTokenOnList(defaultTokens: TokenAddressMap, currency?: Currency): boolean {
  // Use 'address' property check instead of reference equality to avoid module duplication issues
  if (!currency || !('address' in currency)) return true // Native currency is always on list
  const token = currency as Token
  return Boolean(defaultTokens[token.chainId]?.[token.address])
}

// Re-export zeroAddress for compatibility
export const AddressZero = zeroAddress

// Router contract helper (stub - actual implementation uses wagmi hooks)
// This is kept for compatibility but should use useContract hooks instead
export function getRouterContract(
  _chainId: number | undefined,
  _library: unknown,
  _account?: string
): null {
  // This function is deprecated - use wagmi hooks instead
  console.warn('getRouterContract is deprecated, use wagmi hooks instead')
  return null
}
